<script>
    import { onMount } from "svelte";
    import {
        List,
        Section,
        Multipleselect,
        Radio,
        Selector,
        Nav,
    } from "@/components";
    import { toastPopup } from "@utils/common";
    import {
        MEASURE_SYSTEM_OPTIONS,
        GENDER_OPTIONS,
        GOAL_OPTIONS,
        ACTIVITY_OPTIONS,
        ALLERGEN_OPTIONS,
        DISEASES_OPTIONS,
        NAV_NUTRITIONAL_PROFILE,
        NUM_DISHES_OPTIONS,
        EMPTY_USER_BKG,
        TRAININGS,
        MEAL_SCHEDULE,
        DAYS,
        CATEGORIES_OPTIONS,
        PREPARATION_TIME_OPTIONS,
        loadOptions,
    } from "../../../../../contracts";

    import { getBackground, updateBackground } from "@utils/user";
    import { formatContentArray, translate } from "@/utils/format";
    import { trackEvent } from "@/services/firebase";

    let userNutritional = $state(EMPTY_USER_BKG(null));
    let currentTab = $state($NAV_NUTRITIONAL_PROFILE[0]?.id || "profile");
    let toast = $state({});

    onMount(async () => {
        await loadOptions();

        userNutritional = { ...(await getBackground()) };
        trackEvent("nutritional_profile_loaded");
    });

    async function saveUser() {
        toast = toastPopup("User profile updated successfully!", "success");
        await updateBackground({ ...userNutritional });

        //  console.log("User nutritional profile saved:", userNutritional);
    }

    function updateValue(event, name) {
        userNutritional[name] = event;
    }
    function updateList(event, name) {
        userNutritional[name] = event;
    }
</script>

{#snippet section()}
    <Nav
        nav={$NAV_NUTRITIONAL_PROFILE}
        onSelectItem={(e) => (currentTab = e)}
        {currentTab}
    />

    {#if currentTab === "profile"}
        <ul class="section__content">
            <li>
                <label>{translate("MEASURE_SYSTEM", "Measure system")}</label>
                <div class="section__selectorContent">
                    <Radio
                        onSelectItem={(e) => updateValue(e, "measures")}
                        radioName="measures"
                        options={$MEASURE_SYSTEM_OPTIONS}
                        checkedValue={userNutritional.measures}
                    />
                </div>
            </li>
            <li>
                <label>{translate("WEIGHT", "Weight")}</label>
                <input
                    type="number"
                    inputmode="numeric"
                    bind:value={userNutritional.weight}
                />
            </li>
            <li>
                <label>{translate("HEIGHT", "Height")}</label>
                <input
                    type="number"
                    inputmode="numeric"
                    bind:value={userNutritional.height}
                />
            </li>
            <li>
                <label>{translate("BIRTH_DATE", "Birth date")}</label>
                <input type="date" bind:value={userNutritional.birthdate} />
            </li>
            <li>
                <label>{translate("GENDER", "Gender")}</label>
                <div class="section__selectorContent">
                    <Radio
                        onSelectItem={(e) => updateValue(e, "gender")}
                        radioName="gender"
                        options={$GENDER_OPTIONS}
                        checkedValue={userNutritional.gender}
                    />
                </div>
            </li>
            <li>
                <label>{translate("GOAL", "Goal")}</label>
                <Selector
                    onUpdateValue={(e) => updateValue(e, "goal")}
                    selectors={$GOAL_OPTIONS}
                    checkedValue={userNutritional.goal}
                />
            </li>
            <li>
                <label>{translate("ACTIVITY_LEVEL", "Activity level")}</label>
                <Selector
                    onUpdateValue={(e) => updateValue(e, "activityType")}
                    selectors={$ACTIVITY_OPTIONS}
                    checkedValue={userNutritional.activityType}
                />
            </li>
            <li>
                <label>{translate("ALLERGENS", "Allergens")}</label>
                <div class="section__selectorContent">
                    <Multipleselect
                        selectors={$ALLERGEN_OPTIONS}
                        currentSelected={userNutritional.allergens}
                        onUpdateList={(e) => updateList(e, "allergens")}
                    />
                </div>
            </li>
            <li>
                <label>{translate("DISEASES", "Diseases")}</label>
                <div class="section__selectorContent">
                    <Multipleselect
                        selectors={$DISEASES_OPTIONS}
                        currentSelected={userNutritional.diseases}
                        onUpdateList={(e) => updateList(e, "diseases")}
                    />
                </div>
            </li>
        </ul>
    {:else if currentTab === "diet"}
        <ul class="section__content">
            <li>
                <label
                    >{translate("PREPARATION_TIME", "Preparation time")}</label
                >
                <Selector
                    onUpdateValue={(e) => updateValue(e, "preparationTime")}
                    selectors={$PREPARATION_TIME_OPTIONS}
                    checkedValue={userNutritional.preparationTime}
                />
            </li>
            <li>
                <label
                    >{translate("NUMBER_OF_DISHES", "Number of dishes")}</label
                >
                <Selector
                    onUpdateValue={(e) => updateValue(e, "numDishes")}
                    selectors={$NUM_DISHES_OPTIONS}
                    checkedValue={userNutritional.numDishes}
                />
            </li>
            <li>
                <label
                    >{translate(
                        "CATEGORIES_TO_AVOID",
                        "Categories to avoid",
                    )}</label
                >
                <div class="section__selectorContent">
                    <List
                        fields={[
                            { type: "select", options: $CATEGORIES_OPTIONS },
                        ]}
                        rows={formatContentArray(
                            userNutritional?.foodCategoriesToAvoid,
                        )}
                        onUpdateList={(e) =>
                            updateList(e, "foodCategoriesToAvoid")}
                    />
                </div>
            </li>
            <li>
                <label
                    >{translate(
                        "INGREDIENTS_TO_AVOID",
                        "Ingredients to avoid",
                    )}</label
                >
                <div class="section__selectorContent">
                    <List
                        fields={[{ type: "text" }]}
                        rows={formatContentArray(
                            userNutritional?.ingredientsToAvoid,
                        )}
                        onUpdateList={(e) =>
                            updateList(e, "ingredientsToAvoid")}
                    />
                </div>
            </li>
            <li>
                <label>{translate("SCHEDULE_HOURS", "Schedule hours")}</label>
                <div class="section__selectorContent">
                    <List
                        className="listHours"
                        rows={userNutritional.mealSchedule}
                        fields={[
                            { name: translate("HOUR", "Hour"), type: "time" },
                            {
                                name: translate("DISH", "Dish"),
                                type: "select",
                                options: $MEAL_SCHEDULE,
                            },
                        ]}
                        onUpdateList={(e) => updateList(e, "mealSchedule")}
                    />
                </div>
            </li>
        </ul>
    {:else if currentTab === "activity"}
        <ul class="section__content">
            {#each DAYS as day}
                <li>
                    <label
                        >{translate(
                            "TRAINING_DAY",
                            `Training ${day.toLowerCase()}`,
                        )}</label
                    >
                    <div class="section__selectorContent">
                        <List
                            rows={userNutritional[day]}
                            className="listActivity"
                            fields={[
                                {
                                    name: translate(
                                        "INITIAL_TIME",
                                        "Initial time",
                                    ),
                                    type: "time",
                                },
                                {
                                    name: translate("END_TIME", "End time"),
                                    type: "time",
                                },
                                {
                                    name: translate(
                                        "TRAINING_TYPE",
                                        "Training type",
                                    ),
                                    type: "select",
                                    options: $TRAININGS,
                                },
                            ]}
                            onUpdateList={(e) => updateList(e, day)}
                        />
                    </div>
                </li>
            {/each}
        </ul>
    {/if}
{/snippet}

<Section
    headerText="Nutritional profile"
    returnBtn={true}
    actionBack="settings"
    className="nutritionalProfile"
    textBtn="Guardar cambios"
    {section}
    {toast}
    onClickButton={saveUser}
/>

<style lang="scss">
    @use "./styles/index.scss";
</style>
